import { useCallback, useEffect } from 'react';

import { Link } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import Image from 'next/image';

import { Button } from 'Components/LegacyButton';
import { Typography } from 'Components/Typography';

import { upsellBlock as upsellBlockContent } from 'assets/content/checkout/orderConfirmation';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { trackHeapEvent } from 'dux/tracking/actions';
import {
  getHasActiveHaircareSubscription,
  getHasActiveSkincareSubscription,
  getHasCompletedHaircareConsultation,
  getHasCompletedSkincareConsultation,
  getHasEitherHairOrSkinSubscription,
  getHasHairAndSkinSubscriptions,
} from 'dux/user/selectors';

const DesktopGrid = styled.div`
  display: block;
  width: 100%;
  grid-template-columns: repeat(12, 1fr);
  margin: ${legacyTheme.spacing.s32} 0;

  ${legacyTheme.breakpoints.up('sm')} {
    display: grid;
    align-items: center;
    min-height: calc((100vh - 95px) / 2);
    margin: 0;
  }

  ${legacyTheme.breakpoints.up('lg')} {
    padding: 0 ${legacyTheme.spacing.s144};
  }
`;

const FullWidthImageWrapper = styled(DesktopGrid)`
  position: relative;
  z-index: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  grid-column: 2 / 12;
  margin: 0;
  padding: ${legacyTheme.spacing.s56} ${legacyTheme.spacing.s16} 75% ${legacyTheme.spacing.s16};

  color: white;

  text-align: center;

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 0;
    text-align: left;
  }

  ${legacyTheme.breakpoints.up('lg')} {
    padding: 0 ${legacyTheme.spacing.s144};
  }
`;

const BottomImage = styled(Image)`
  position: absolute;
  top: 0;
  bottom: 10px;
  left: 0;
  right: 0;
  object-fit: cover;
  object-position: 66.7% 90%;

  width: 100%;
  height: 100%;

  ${legacyTheme.breakpoints.up('lg')} {
    object-position: center;
  }
`;

const TextBlock = styled.div`
  z-index: inherit;

  grid-column: 2 / 12;

  span {
    display: inline;
  }
`;

const TitleRecommendation = styled(Typography)`
  margin-bottom: ${legacyTheme.spacing.s16};
  color: ${theme.colors.primary[300]};
  ${legacyTheme.breakpoints.up('sm')} {
    max-width: 40%;
  }
`;

type CheckoutBlockReferFriendProps = {
  category: 'haircare' | 'skincare' | undefined;
};

export const CheckoutBlockReferFriend = ({ category }: CheckoutBlockReferFriendProps) => {
  const dispatch = useAppDispatch();
  const hasCompletedHaircareConsultation = useAppSelector(getHasCompletedHaircareConsultation);
  const hasCompletedSkincareConsultation = useAppSelector(getHasCompletedSkincareConsultation);
  const hasActiveHaircareSubscription = useAppSelector(getHasActiveHaircareSubscription);
  const hasActiveSkincareSubscription = useAppSelector(getHasActiveSkincareSubscription);
  const hasHairAndSkinSubscriptions = useAppSelector(getHasHairAndSkinSubscriptions);
  const hasEitherHairOrSkinSubscription = useAppSelector(getHasEitherHairOrSkinSubscription);

  const computeUpsellBlockSecondaryBlock = useCallback(() => {
    const {
      tracking,
      img,
      title: { referFriends },
      cta: { referFriend: referFriendCta },
    } = upsellBlockContent;

    // Upsell block text
    const upsellBlockText = referFriends;

    // Upsell block CTA
    const upsellBlockCTA = referFriendCta;

    return {
      img,
      upsellBlockCTA,
      upsellBlockText,
      tracking,
    };
  }, [
    hasCompletedHaircareConsultation,
    hasCompletedSkincareConsultation,
    hasActiveHaircareSubscription,
    hasActiveSkincareSubscription,
    hasHairAndSkinSubscriptions,
    hasEitherHairOrSkinSubscription,
    category,
  ]);

  const { img, upsellBlockCTA, upsellBlockText, tracking } = computeUpsellBlockSecondaryBlock();

  const { isMobile, isTablet, isDesktop } = useResponsiveVariant();

  useEffect(() => {
    dispatch(trackHeapEvent(tracking.pageView));
  }, []);

  return (
    <FullWidthImageWrapper data-testid="upsell-block">
      {isMobile && (
        <BottomImage
          alt="Prose assortment of custom haircare and skincare products"
          src={img.mobile}
        />
      )}
      {isTablet && (
        <BottomImage
          alt="Prose assortment of custom haircare and skincare products"
          src={img.tablet}
        />
      )}
      {isDesktop && (
        <BottomImage
          alt="Prose assortment of custom haircare and skincare products"
          src={img.desktop}
        />
      )}
      <TextBlock>
        <TitleRecommendation markupName="p" variant="h1r">
          <span>{upsellBlockText}</span>
        </TitleRecommendation>
        <Button
          Component={Link}
          data-click={upsellBlockCTA.dataClick}
          data-from={upsellBlockCTA.dataFrom}
          to={upsellBlockCTA.url}
          variant="vert"
        >
          {upsellBlockCTA.label}
        </Button>
      </TextBlock>
    </FullWidthImageWrapper>
  );
};
